import { useTranslation } from "react-i18next";
import {
  ADMIN_ONLY_VIEWS,
  OPERATIONAL_VIEWS,
  viewsForRole,
  type AppView,
  type AssignableRole,
} from "@/contracts";

/**
 * Checklist of modules grantable for the selected role. Admin-only views are
 * hidden when the role is USER so the form cannot offer an illegal grant.
 */
export function ViewAccessFields({
  role,
  value,
  onChange,
  disabled = false,
}: {
  role: AssignableRole;
  value: readonly AppView[];
  onChange: (next: AppView[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("pages");
  const ceiling = new Set(viewsForRole(role));
  const selected = new Set(value);
  const options = role === "ADMIN" ? APP_VIEW_ORDER : OPERATIONAL_VIEWS;

  function toggle(view: AppView, checked: boolean) {
    const next = APP_VIEW_ORDER.filter((entry) => {
      if (entry === view) {
        return checked;
      }

      return selected.has(entry);
    }).filter((entry) => ceiling.has(entry));

    onChange(next);
  }

  return (
    <fieldset className="flex flex-col gap-2 border-0 p-0">
      <legend className="text-sm font-medium text-ink">{t("fieldViewAccess")}</legend>
      <p className="text-xs text-ink-muted">{t("fieldViewAccessHint")}</p>
      <div className="flex flex-col gap-2">
        {options.map((view) => {
          const id = `view-access-${view}`;

          return (
            <label key={view} htmlFor={id} className="flex items-center gap-2 text-sm text-ink">
              <input
                id={id}
                type="checkbox"
                className="size-4 rounded border-border-subtle"
                checked={selected.has(view)}
                disabled={disabled}
                onChange={(event) => toggle(view, event.currentTarget.checked)}
              />
              {t(`viewLabels.${view}`)}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

const APP_VIEW_ORDER: readonly AppView[] = [...OPERATIONAL_VIEWS, ...ADMIN_ONLY_VIEWS];
